import React, {
  PropsWithChildren,
  useCallback,
  useMemo,
  useState,
} from 'react';

interface ModalContextType {
  isAnyModalOpen: boolean;
  isModalOpen: (name: string) => boolean;
  setModalOpen: (name: string, open?: boolean) => void;
}

export const ModalContext = React.createContext<ModalContextType>(
  {} as ModalContextType
);

type ModalProviderProps = PropsWithChildren;

export const ModalProvider: React.FC<ModalProviderProps> = ({ children }) => {
  const [openModals, setOpenModals] = useState<Set<string>>(new Set());

  const isModalOpen = useCallback(
    (name: string) => openModals.has(name),
    [openModals]
  );

  const setModalOpen = useCallback((name: string, open = true) => {
    if (open) {
      setOpenModals((prev) => new Set(prev.add(name)));
    } else {
      setOpenModals((prev) => new Set([...prev].filter((n) => n !== name)));
    }
  }, []);

  const isAnyModalOpen = useMemo(() => openModals.size > 0, [openModals.size]);

  const value = useMemo(
    () => ({
      isAnyModalOpen,
      isModalOpen,
      setModalOpen,
    }),
    [isAnyModalOpen, isModalOpen, setModalOpen]
  );

  return (
    <ModalContext.Provider value={value}>
      {children}
    </ModalContext.Provider>
  );
};

export const useModal = () => React.useContext(ModalContext);
